// @flow
import React, { useContext } from 'react';
import classnames from 'classnames';
import { isEmpty } from 'lodash';

import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import ReactAppContext from 'Context/ReactAppContext';
import { getCurrencySymbol } from 'Components/utils/price/price.util';

import { type BadgeProps } from 'Components/frameworks/badge/Badge.jsx';
import {
  DownloadLinksList,
  type DownloadLinksListItem,
} from 'Components/frameworks/download-links-list/DownloadLinksList.jsx';
import { Icon, type IconName } from 'Components/frameworks/icon/Icon.jsx';
import { Sheet } from 'Components/frameworks/sheet/Sheet.jsx';
import { TemplateString } from 'Components/frameworks/template-string/TemplateString.jsx';
import { Text } from 'Components/frameworks/text/Text.jsx';
import { Title } from 'Components/frameworks/title/Title.jsx';
import { t, getKeyValue } from 'Src/i18n';

import { ProductFeatureDetailBrandInformation } from './product-feature-detail-brand-information/ProductFeatureDetailBrandInformation.jsx';
import { ProductFeatureDetailFacilities } from './product-feature-detail-facilities/ProductFeatureDetailFacilities.jsx';
import { ProductFeatureDetailInformations } from './product-feature-detail-informations/ProductFeatureDetailInformations.jsx';
import {
  ProductFeatureDetailMainInformation,
  type ProductFeatureDetailMainInformationProps,
} from './product-feature-detail-main-information/ProductFeatureDetailMainInformation.jsx';
import { ProductFeatureDetailServices } from './product-feature-detail-services/ProductFeatureDetailServices.jsx';

import { type ProductFeatureDetailServiceProps } from './product-feature-detail-services/ProductFeatureDetailService.jsx';
import { type ProductFeatureDetailInformationProps } from './product-feature-detail-informations/ProductFeatureDetailInformation.jsx';
import { type ProductFeatureDetailFacilityProps } from './product-feature-detail-facilities/ProductFeatureDetailFacility.jsx';

export type ProductFeatureDetailProps = {
  brandInformation?: ?{
    description: ?string,
    imageSrc: ?string,
    title: string,
  },
  description?: string,
  diamond?: BadgeProps,
  downloadLinksList?: Array<DownloadLinksListItem>,
  equipmentsTitle?: string,
  equipments?: ?Array<{
    items: ?Array<{
      iconName?: IconName,
      label: string,
    }>,
    title: string,
  }>,
  facilitiesTitle?: string,
  mainInformationTitle?: string,
  mainInformation?: ProductFeatureDetailMainInformationProps,
  generalInformationTitle?: string,
  generalInformation?: Array<ProductFeatureDetailInformationProps>,
  generalInformationExtraCost?: string,
  immersive: *,
  isIncluded?: boolean,
  notIncludedMessage?: string,
  includedMessage?: string,
  includedServices?: Array<ProductFeatureDetailServiceProps>,
  includedTitle?: string,
  insideFacilities?: Array<ProductFeatureDetailFacilityProps>,
  insideFacilitiesTitle?: string,
  onDemandServices?: Array<ProductFeatureDetailServiceProps>,
  onDemandTitle?: string,
  outsideFacilities?: Array<ProductFeatureDetailFacilityProps>,
  outsideFacilitiesTitle?: string,
  targetPrice?: {
    currency: string,
    label: string,
    price: number,
  },
  title: string,
  titleColor?: Colors,
};

export function ProductFeatureDetail(props: ProductFeatureDetailProps) {
  const {
    brandInformation,
    description,
    diamond,
    downloadLinksList = [],
    facilitiesTitle = '',
    mainInformationTitle,
    mainInformation,
    equipments,
    equipmentsTitle,
    generalInformationTitle,
    generalInformation = [],
    generalInformationExtraCost,
    immersive,
    isIncluded,
    includedMessage,
    notIncludedMessage,
    includedServices = [],
    includedTitle,
    insideFacilities = [],
    insideFacilitiesTitle,
    onDemandServices = [],
    onDemandTitle,
    outsideFacilities = [],
    outsideFacilitiesTitle,
    targetPrice = {},
    title,
    titleColor = COLORS.BLUE,
  } = props;

  const { locale } = useContext(ReactAppContext);

  const footer = (
    <ProductFeatureDetailInformations
      className="my-3"
      items={generalInformation}
      notes={generalInformationExtraCost}
      title={generalInformationTitle}
    />
  );

  const message = isIncluded ? includedMessage : notIncludedMessage;

  const instalmentValue = getKeyValue('common.price.instalmentValue');

  return (
    <Sheet diamond={diamond} footer={footer} immersive={immersive}>
      {!!title && (
        <Title className={message ? 'mb-2' : 'mb-8'} theme="title-sans-8" color={titleColor}>
          {title}
        </Title>
      )}
      {!!message && (
        <p
          className={classnames(
            'reset-paragraph flex items-center justify-center font-brand mb-8 text-center text-lg sm:text-3xl',
            {
              'text-blue': isIncluded,
              'text-amber': !isIncluded,
            },
          )}
        >
          <Icon
            className="rtl:mr-2"
            name={isIncluded ? ICONS.ALL_INCLUSIVE : ICONS.ON_DEMAND}
            width="1em"
          />
          {message}
        </p>
      )}
      <Text className="mb-8" text={description} />
      <DownloadLinksList className="mb-8" links={downloadLinksList} theme="lite" />
      <ProductFeatureDetailMainInformation
        className="my-5"
        smallTitle={
          targetPrice.price ? (
            <TemplateString
              className="text-base font-sans"
              template={targetPrice.label}
              values={{
                currency: getCurrencySymbol(locale, targetPrice.currency),
                price: getKeyValue('common.price.displayInstalmentsPrice')
                  ? t('common.price.instalmentPrice', {
                      instalmentValue,
                      dividedPrice: Math.trunc(targetPrice.price / instalmentValue),
                    })
                  : Math.trunc(targetPrice.price),
              }}
              valuesClassName={{
                currency: 'text-amber text-xl font-bold',
                price: 'text-amber text-xl font-bold',
              }}
            />
          ) : null
        }
        title={mainInformationTitle}
        {...mainInformation}
      />
      <ProductFeatureDetailBrandInformation
        className="mt-5 mb-10 sm:mb-20"
        description={brandInformation?.description}
        imageSrc={brandInformation?.imageSrc}
        title={brandInformation?.title}
      />
      {!!includedServices.length && (
        <ProductFeatureDetailServices
          className="my-5"
          color={COLORS.BLUE}
          equipments={equipments}
          equipmentsTitle={equipmentsTitle}
          items={includedServices}
          title={includedTitle}
          titleIcon={ICONS.ALL_INCLUSIVE}
        />
      )}
      {!!onDemandServices.length && (
        <ProductFeatureDetailServices
          className="my-5"
          color={COLORS.AMBER}
          items={onDemandServices}
          title={onDemandTitle}
          titleIcon={ICONS.ON_DEMAND}
        />
      )}
      {(!isEmpty(insideFacilities) || !isEmpty(outsideFacilities)) && (
        <section>
          <Title className="my-5" hLevel={3} theme="title-brand-6">
            {facilitiesTitle}
          </Title>
          {insideFacilities.length && (
            <ProductFeatureDetailFacilities
              className="my-5"
              items={insideFacilities}
              title={insideFacilitiesTitle}
            />
          )}
          {outsideFacilities && (
            <ProductFeatureDetailFacilities
              className="my-5"
              items={outsideFacilities}
              title={outsideFacilitiesTitle}
            />
          )}
        </section>
      )}
    </Sheet>
  );
}
